import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Plus, ShoppingCart } from "lucide-react";
import OrderTimeline, { statusColor } from "@/components/OrderTimeline";
import { formatTime } from "@/lib/format";

/**
 * Renders the live order tracker tab content. Customer-facing.
 * Each item shows its status (received/preparing/ready/served) with a timeline.
 */
export default function OrderTracker({ orders, onAddMore, canOrderMore }) {
    if (!orders.length) {
        return (
            <Card>
                <CardContent className="p-10 text-center">
                    <ShoppingCart className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="font-medium">No orders yet</p>
                    <p className="text-sm text-muted-foreground">
                        Browse the menu and place your first order.
                    </p>
                    {canOrderMore && (
                        <Button className="mt-4" onClick={onAddMore} data-testid="go-to-menu-button">
                            Open menu
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {orders.map((order, index) => (
                <OrderCard key={order.id} order={order} index={index} />
            ))}
            {canOrderMore && (
                <div className="flex justify-center">
                    <Button variant="secondary" onClick={onAddMore} data-testid="order-more-button">
                        <Plus className="h-4 w-4 mr-1" /> Order more items
                    </Button>
                </div>
            )}
        </div>
    );
}

function OrderCard({ order, index }) {
    return (
        <Card data-testid="order-card">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Order #{index + 1}
                        </div>
                        <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
                            <Clock className="h-3 w-3" />
                            {formatTime(order.created_at)}
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {order.items.length} item{order.items.length === 1 ? "" : "s"}
                    </div>
                </div>
                <div className="space-y-3">
                    {order.items.map((item) => (
                        <OrderItemRow key={item.id} item={item} />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

function OrderItemRow({ item }) {
    return (
        <div className="p-3 rounded-lg border border-border" data-testid="order-item-row">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-medium">
                        {item.quantity}× {item.name}
                    </div>
                    {item.notes && (
                        <div className="text-xs text-muted-foreground italic">“{item.notes}”</div>
                    )}
                </div>
                <Badge
                    variant="outline"
                    className={`capitalize ${statusColor(item.status)}`}
                    data-testid="order-item-status"
                >
                    {item.status}
                </Badge>
            </div>
            <div className="mt-3">
                <OrderTimeline status={item.status} />
            </div>
        </div>
    );
}
